import type { ComponentProps, JSX, ReactNode } from 'react';
import { styled } from 'styled-components';
import { isNotNil } from '@wistia/type-guards';
import { cloneElement } from 'react';
import type {
  ButtonAsButtonProps,
  ButtonAsLinkProps,
  ButtonProps,
  ButtonSizes,
} from '../Button/Button';
import { IconButton } from '../IconButton';
import { Icon } from '../Icon';
import { Menu } from '../Menu';
import { Button } from '../Button';
import type { ResponsiveObject } from '../../private/hooks/useResponsiveProp/useResponsiveProp';
import type { ButtonVariants } from '../Button/buttonStyles';
import { getColorScheme } from '../../private/helpers/getColorScheme';
import type { ColorSchemeTypes } from '../ColorSchemeWrapper';

const StyledSplitButton = styled.span<{ $colorScheme: ColorSchemeTypes }>`
  ${({ $colorScheme }) => getColorScheme($colorScheme)};
  white-space: nowrap;

  &:has(:nth-child(2)) {
    > [data-wui-button='true']:first-child {
      border-top-right-radius: 0;
      border-bottom-right-radius: 0;
      padding-right: 0;
    }

    > [data-wui-button='true']:last-child {
      border-top-left-radius: 0;
      border-bottom-left-radius: 0;
      display: inline-flex;
    }
  }
`;
export type SplitButtonProps = {
  /**
   * The text for the primary button
   */
  children?: ButtonProps['children'];

  colorScheme?: ButtonProps['colorScheme'];
  /**
   * The icon to use for the menu toggle, defaults to a down caret
   * @default <Icon type="caret-down" />
   */
  menuIcon?: JSX.Element;

  /**
   * The menu items to render within the menu. If the secondary behavior of `SplitButton` should be a button, rather than a menu, omit `menuItems` and use `secondaryAction` instead.
   */
  menuItems?: ReactNode;

  /**
   * The label to use for the menu, only for screenreaders
   */
  menuLabel?: string;

  /**
   * If using `menuItems`, any additional props to pass to the `Menu` component
   */
  menuProps?: Omit<ComponentProps<typeof Menu>, 'children' | 'disabled' | 'trigger'>;

  /**
   * Secondary action should not be used when menuItems is set
   */
  secondaryAction?: JSX.Element;

  /**
   * The size of button to display. Can be used as a [responsive prop](/docs/ui-docs-responsive-props--docs)
   */
  size?: ButtonSizes | ResponsiveObject<ButtonSizes>;

  /**
   * A visual style for the button
   */
  variant?: ButtonVariants;
} & (ButtonAsButtonProps | ButtonAsLinkProps);

/**
 * A SplitButton is an extension of [Button]() that adds a menu to the right hand side of the button.
 */
export const SplitButton = ({
  children,
  menuLabel = 'Select an option',
  menuIcon = <Icon type="caret-down" />,
  menuItems,
  disabled = false,
  colorScheme = 'inherit',
  variant = 'solid',
  secondaryAction,
  size = 'md',
  unstyled = false,
  menuProps = {},
  ...props
}: SplitButtonProps): JSX.Element => {
  return (
    <StyledSplitButton $colorScheme={colorScheme}>
      <Button
        disabled={disabled}
        size={size}
        unstyled={unstyled}
        variant={variant}
        {...props}
      >
        {children}
      </Button>
      {isNotNil(menuItems) && (
        <Menu
          {...menuProps}
          disabled={disabled}
          trigger={
            <IconButton
              disabled={disabled}
              label={menuLabel}
              size={size}
              unstyled={unstyled}
              variant={variant}
            >
              {menuIcon}
            </IconButton>
          }
        >
          {menuItems}
        </Menu>
      )}
      {isNotNil(secondaryAction) &&
        cloneElement(secondaryAction, { disabled, size, unstyled, variant, colorScheme })}
    </StyledSplitButton>
  );
};

SplitButton.displayName = 'SplitButton';
